import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { USERS, STORIES, loadSocialState, saveSocialState, formatRelativeTime, formatNumber } from './socialData';
import PostCard from './PostCard';
import './SocialFeed.css';

function SocialFeed() {
  const navigate = useNavigate();
  const [state, setState] = useState(loadSocialState);
  const [newPostText, setNewPostText] = useState('');
  const [activeTab, setActiveTab] = useState('feed');
  const [expandedComments, setExpandedComments] = useState({});

  const update = useCallback((newState) => {
    setState(newState);
    saveSocialState(newState);
  }, []);

  const handleLike = useCallback((postId) => {
    setState((prev) => {
      const newPosts = prev.posts.map((p) =>
        p.id === postId ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p
      );
      const ns = { ...prev, posts: newPosts };
      saveSocialState(ns);
      return ns;
    });
  }, []);

  const handleBookmark = useCallback((postId) => {
    setState((prev) => {
      const newPosts = prev.posts.map((p) =>
        p.id === postId ? { ...p, bookmarked: !p.bookmarked } : p
      );
      const ns = { ...prev, posts: newPosts };
      saveSocialState(ns);
      return ns;
    });
  }, []);

  const handleComment = useCallback((postId, text) => {
    setState((prev) => {
      const newComment = {
        id: Date.now().toString(),
        userId: 'user1',
        text,
        timestamp: Date.now(),
        likes: 0,
      };
      const postComments = prev.comments[postId] || [];
      const newPosts = prev.posts.map((p) =>
        p.id === postId ? { ...p, comments: p.comments + 1 } : p
      );
      const ns = {
        ...prev,
        posts: newPosts,
        comments: { ...prev.comments, [postId]: [...postComments, newComment] },
      };
      saveSocialState(ns);
      return ns;
    });
  }, []);

  const handleNewPost = () => {
    if (!newPostText.trim()) return;
    const newPost = {
      id: Date.now().toString(),
      userId: 'user1',
      timestamp: Date.now(),
      text: newPostText.trim(),
      likes: 0, comments: 0, shares: 0,
      bookmarked: false, liked: false,
    };
    const ns = { ...state, posts: [newPost, ...state.posts] };
    update(ns);
    setNewPostText('');
  };

  const handleReset = () => {
    localStorage.removeItem('demohub_social');
    setState(loadSocialState());
    setExpandedComments({});
  };

  const toggleComments = (postId) => {
    setExpandedComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const displayPosts = activeTab === 'bookmarks'
    ? state.posts.filter((p) => p.bookmarked)
    : state.posts;

  return (
    <div className="social-app">
      <div className="social-topbar">
        <div className="social-topbar-left">
          <button className="social-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="social-title">Feed</h1>
        </div>
        <div className="social-tabs">
          <button className={`social-tab ${activeTab === 'feed' ? 'active' : ''}`} onClick={() => setActiveTab('feed')}>
            Feed
          </button>
          <button className={`social-tab ${activeTab === 'bookmarks' ? 'active' : ''}`} onClick={() => setActiveTab('bookmarks')}>
            Bookmarks
          </button>
        </div>
        <button className="social-reset-btn" onClick={handleReset}>↻ Reset</button>
      </div>

      <div className="social-layout">
        <div className="social-main">
          {/* Stories */}
          <div className="social-stories">
            <div className="social-story social-story-add">
              <div className="social-story-avatar add">
                <span>+</span>
              </div>
              <span className="social-story-name">Your Story</span>
            </div>
            {STORIES.map((story) => {
              const user = USERS[story.userId];
              return (
                <div key={story.userId} className={`social-story ${story.seen ? 'seen' : ''}`}>
                  <div className="social-story-avatar">
                    <span>{user.avatar}</span>
                  </div>
                  <span className="social-story-name">{user.name.split(' ')[0]}</span>
                </div>
              );
            })}
          </div>

          {/* Compose */}
          <div className="social-compose">
            <div className="social-compose-header">
              <span className="social-compose-avatar">{USERS.user1.avatar}</span>
              <textarea
                placeholder="What's on your mind?"
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                rows={2}
              />
            </div>
            <div className="social-compose-footer">
              <div className="social-compose-actions">
                <button className="social-compose-action" title="Photo">🖼️</button>
                <button className="social-compose-action" title="GIF">🎬</button>
                <button className="social-compose-action" title="Poll">📊</button>
                <button className="social-compose-action" title="Emoji">😀</button>
              </div>
              <button
                className={`social-post-btn ${newPostText.trim() ? 'active' : ''}`}
                onClick={handleNewPost}
                disabled={!newPostText.trim()}
              >
                Post
              </button>
            </div>
          </div>

          {/* Posts */}
          <div className="social-posts">
            {displayPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                user={USERS[post.userId]}
                comments={state.comments[post.id] || []}
                users={USERS}
                showComments={expandedComments[post.id]}
                onLike={() => handleLike(post.id)}
                onBookmark={() => handleBookmark(post.id)}
                onComment={(text) => handleComment(post.id, text)}
                onToggleComments={() => toggleComments(post.id)}
                formatRelativeTime={formatRelativeTime}
                formatNumber={formatNumber}
              />
            ))}
            {displayPosts.length === 0 && (
              <div className="social-empty">
                {activeTab === 'bookmarks' ? 'No bookmarked posts yet.' : 'No posts yet.'}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="social-sidebar">
          <div className="social-sidebar-card">
            <h3>Trending Topics</h3>
            <div className="social-trending">
              {['#javascript', '#reactjs', '#webdev', '#AI', '#opensource', '#coding'].map((tag) => (
                <span key={tag} className="social-trending-tag">{tag}</span>
              ))}
            </div>
          </div>
          <div className="social-sidebar-card">
            <h3>Suggested Connections</h3>
            <div className="social-suggestions">
              {Object.values(USERS).filter((u) => u.id !== 'user1').slice(0, 4).map((user) => (
                <div key={user.id} className="social-suggestion">
                  <span className="social-suggestion-avatar">{user.avatar}</span>
                  <div className="social-suggestion-info">
                    <span className="social-suggestion-name">
                      {user.name}
                      {user.verified && <span className="social-verified" title="Verified">✓</span>}
                    </span>
                    <span className="social-suggestion-handle">{user.handle}</span>
                  </div>
                  <button className="social-follow-btn">Follow</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SocialFeed;
